import { useState } from 'react';
import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';

type StyleConfig = {
  backgroundColor?: string;
  iconColor?: string;
};

interface BannerProps {
  buttonLabel: string;
  description: string;
  iconName: IconName;
  onButtonClick?: () => void;
  styleConfig?: StyleConfig;
  title: string;
  trackingIdBase: string;
}

const defaultStyleConfig: StyleConfig = {
  backgroundColor: theme.colors.warning5,
};

export const Banner = ({
  buttonLabel,
  description,
  iconName,
  onButtonClick,
  styleConfig,
  title,
  trackingIdBase,
}: BannerProps) => {
  const [show, setShow] = useState<boolean>(true);

  if (!show) {
    return null;
  }

  return (
    <div
      css={styles.mainWrapper}
      style={{ backgroundColor: styleConfig?.backgroundColor || defaultStyleConfig.backgroundColor }}
    >
      <Icon
        name={iconName}
        style={{
          color: styleConfig?.iconColor,
          minWidth: theme.spacing(3),
        }}
      />
      <div css={styles.contentWrapper}>
        <div>
          <Heading level={3} style={{ fontSize: theme.font.size.large }}>
            {title}
          </Heading>
          <Text>{description}</Text>
        </div>
        <Button onClick={onButtonClick} trackingId={`${trackingIdBase}-button`} variant='tertiary'>
          {buttonLabel}
        </Button>
      </div>
      <Button iconName='x' onClick={() => setShow(false)} size='large' variant='secondary' />
    </div>
  );
};

const styles = {
  mainWrapper: css`
    box-shadow: ${theme.shadows.light};
    display: flex;
    gap: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(0.5)};
    padding: ${theme.spacing(2)};

    @container (min-width: ${breakpoints.mobile.max}px) {
      align-items: center;
    }
  `,

  contentWrapper: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: ${theme.spacing(1)};
    justify-content: flex-start;

    @container (min-width: ${breakpoints.mobile.max}px) {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
    }

    > button {
      width: fit-content;
    }
  `,
};
